import { CachedLearningAudio } from '@shared/media/audio/CachedLearningAudio'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Pause, Play, Volume2 } from 'lucide-react'
import type { LearningContentItem, LearningVocabularyItem } from '@services/firebase/learning'
import {
  resolveLocalizedMapText,
  resolveTranslatedText,
} from '@features/learning/shared/learning-helpers'
import { LmsButton } from '@shared/ui'
import type { LearningTemplateResult } from './learning-template-contract'
import { LearningTemplateResultBar } from './learning-template-result-bar'
import { resolveLearningVocabularyTitle } from './learning-vocabulary-title'

function LearningVocabularyTile({
  item,
  locale,
  onAudioEnded,
  playNow = false,
  showTranslation,
  sortIndex,
}: {
  readonly item: LearningVocabularyItem
  readonly locale: string
  readonly onAudioEnded?: () => void
  readonly playNow?: boolean
  readonly showTranslation: boolean
  readonly sortIndex: number
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [countdownSeconds, setCountdownSeconds] = useState(3)
  const title = item.text ?? resolveLearningVocabularyTitle(item, locale)
  const translation =
    resolveLocalizedMapText(item.translate, locale) ??
    resolveTranslatedText(item.translates, locale)

  useEffect(() => {
    if (!playNow || !item.audio) {
      return undefined
    }

    const interval = window.setInterval(() => {
      setCountdownSeconds((previous) => Math.max(previous - 1, 0))
    }, 1000)
    const timeout = window.setTimeout(() => {
      void audioRef.current?.play().catch(() => setIsPlaying(false))
    }, 3000)
    return () => {
      window.clearInterval(interval)
      window.clearTimeout(timeout)
    }
  }, [item.audio, playNow])

  return (
    <div
      className={`boxVocabulary${showTranslation ? '' : ' hideTranslate'}${playNow ? ' playNow' : ''}`}
      onClick={() => {
        if (!item.audio) {
          return
        }
        if (audioRef.current?.paused) {
          void audioRef.current.play().catch(() => undefined)
          return
        }
        audioRef.current?.pause()
      }}
    >
      <div className={`column1${isPlaying ? ' isPlaying' : ''}`}>
        <div
          className={`content${translation && !showTranslation ? ' hideTranslate' : ''}${
            item.image ?? item.featuredImage ? '' : ' withoutImage'
          }`}
        >
          {item.image ?? item.featuredImage ? (
            <img
              alt={title ?? ''}
              className="image"
              src={item.image ?? item.featuredImage}
            />
          ) : null}
          <span>{title}</span>
          {!showTranslation && !playNow ? <span className="sort">{sortIndex + 1}</span> : null}
        </div>
      </div>
      {showTranslation && translation ? (
        <div className={`column2${isPlaying ? ' isPlaying' : ''}`}>
          <div className="content">{translation}</div>
        </div>
      ) : null}
      {playNow ? (
        <div className="countDownBox">
          {countdownSeconds > 0 ? (
            <span className="time legacySelfStudyVocabularyCountdown">{countdownSeconds}</span>
          ) : (
            <span className="btnIcon audio-playing">
              <Volume2 aria-hidden="true" className="legacySelfStudyVocabularyPlaying" />
            </span>
          )}
        </div>
      ) : item.audio && isPlaying ? (
        <div className="svgIconPlay">
          <span className="btnIcon audio-playing">
            <Volume2 aria-hidden="true" className="legacySelfStudyVocabularyPlaying" />
          </span>
        </div>
      ) : null}
      {item.audio ? (
        <CachedLearningAudio
          onEnded={() => {
            setIsPlaying(false)
            window.setTimeout(() => onAudioEnded?.(), 1000)
          }}
          onPause={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
          ref={audioRef}
          src={item.audio}
        />
      ) : null}
    </div>
  )
}

export function LearningVocabularyTemplate({
  continuePending = false,
  continuePendingLabel,
  item,
  locale,
  onContinue,
  sourceOwner,
}: {
  readonly continuePending?: boolean
  readonly continuePendingLabel?: ReactNode
  readonly item: LearningContentItem
  readonly locale: string
  readonly onContinue: (result: LearningTemplateResult) => void
  readonly sourceOwner: string
}) {
  const vocabularies = item.vocabularies ?? []
  const [stepActive, setStepActive] = useState(0)
  const [playNow, setPlayNow] = useState(false)
  const [playIndex, setPlayIndex] = useState(0)

  if (vocabularies.length === 0) {
    return (
      <LearningTemplateResultBar
        continuePending={continuePending}
        continuePendingLabel={continuePendingLabel}
        finished={false}
        onNext={() => onContinue('correct')}
      />
    )
  }

  const canAutoPlay = vocabularies.every((vocabulary) => Boolean(vocabulary.audio))
  const handleNextAudio = () => {
    if (playIndex < vocabularies.length - 1) {
      setPlayIndex((previous) => previous + 1)
      return
    }
    setPlayNow(false)
  }
  const handleAdvance = () => {
    if (stepActive < 2) {
      setStepActive((previous) => previous + 1)
      return
    }
    onContinue('correct')
  }
  const displayedVocabularies = playNow
    ? vocabularies[playIndex]
      ? [{ index: playIndex, vocabulary: vocabularies[playIndex] }]
      : []
    : vocabularies.map((vocabulary, index) => ({ index, vocabulary }))

  return (
    <div
      className="legacySelfStudyVocabularyTemplate"
      data-legacy-owner={sourceOwner}
      data-legacy-template="12"
      data-qa="learning-template-12-vocabulary"
    >
      <div className="boxWrapAnswer legacySelfStudyVocabularies">
        {stepActive === 2 && canAutoPlay ? (
          <div className="legacySelfStudyVocabularyPlayControl">
            <LmsButton
              aria-label={playNow ? 'Pause vocabulary audio' : 'Play vocabulary audio'}
              onClick={() => {
                if (playNow) {
                  setPlayNow(false)
                  return
                }
                setPlayIndex(0)
                setPlayNow(true)
              }}
              size="icon-lg"
              type="button"
              variant="secondary"
            >
              {playNow ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
            </LmsButton>
          </div>
        ) : null}
        <div className="legacySelfStudyVocabularyList">
          {displayedVocabularies.map(({ index, vocabulary }) => (
            <LearningVocabularyTile
              item={vocabulary}
              key={`${item.id}-vocabulary-${vocabulary.id ?? index}-${playNow ? `play-${index}` : `step-${stepActive}`}`}
              locale={locale}
              onAudioEnded={handleNextAudio}
              playNow={playNow}
              showTranslation={stepActive === 0}
              sortIndex={index}
            />
          ))}
        </div>
      </div>
      <LearningTemplateResultBar
        continuePending={continuePending}
        continuePendingLabel={continuePendingLabel}
        finished={false}
        onNext={handleAdvance}
      />
    </div>
  )
}
